import React, { useMemo, useEffect } from 'react';
import { Button, Header, LoadingData } from '@hwm/ui-lib';
import { useSidebar } from '@shared/hooks';
import { SidebarKeys, useSidebarContent } from './Sidebar.utils';
import { useSitesDataSource } from './SiteList/sitesDataSource';
import { useSiteListState } from './SiteList/useSiteListFiltersState';
import { MenuOpen } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core';
import { MENU_ICON } from '../Layout/AppLayout';
import { Trans } from '@lingui/macro';

const useStyles = makeStyles((theme) => ({
  menuToggler: {
    width: MENU_ICON,
    minWidth: MENU_ICON,
    minHeight: MENU_ICON,
    height: MENU_ICON,
    [theme.breakpoints.down('xs')]: {
      width: MENU_ICON - 4,
      minWidth: MENU_ICON - 4,
      minHeight: MENU_ICON - 4,
      height: MENU_ICON - 4,
    },
    cursor: 'pointer',
    marginRight: theme.spacing(3),
    transition: 'transform 0.3s',
    transform: ({ show }: { show: boolean }) => (show ? 'rotate(180deg)' : 'rotate(0)'),
  },
}));

export const Sidebar: React.FC = () => {
  const { sidebarData } = useSidebarContent();

  const {
    showSidebarData,
    sidebar: { state, section },
    handleSidebarSection,
    handleSidebarState,
  } = useSidebar();

  const classes = useStyles({ show: state });

  const { setSearchFilter } = useSiteListState();
  const { handleSetEmptySitesData, loading } = useSitesDataSource();

  const content = useMemo(() => {
    return sidebarData[section];
  }, [sidebarData, section]);

  const sitesOrAccountsSection = useMemo(
    () => section === SidebarKeys.accounts || section === SidebarKeys.siteList,
    [section]
  );

  // set Ids to [] - fix problem with duplicated sites
  // redux sometimes holds siteIds from different account
  useEffect(() => {
    if (section === SidebarKeys.accounts) {
      handleSetEmptySitesData();
      setSearchFilter('');
    }
  }, [section, setSearchFilter, handleSetEmptySitesData]);

  return (
    <div
      className="flex flex-col justify-start w-full h-full max-h-full max-w-full overflow-hidden pb-0.5"
      data-testid="sidebar-wrapper"
    >
      <Header
        icon={
          <MenuOpen
            className={classes.menuToggler}
            onClick={() => handleSidebarState(false)}
            data-testid="sidebar-header-toggler-button"
          />
        }
        title={content.header}
        buttons={
          <>
            {sitesOrAccountsSection ? (
              <>
                <Button
                  variant={section === SidebarKeys.siteList ? 'contained' : 'outlined'}
                  onClick={() => handleSidebarSection(SidebarKeys.siteList)}
                  style={{}}
                  disabled={loading}
                  dataTestId="sidebar-header-buttons-site-list-button"
                >
                  <Trans>Site List</Trans>
                </Button>
                <Button
                  variant={section === SidebarKeys.accounts ? 'contained' : 'outlined'}
                  onClick={() => handleSidebarSection(SidebarKeys.accounts)}
                  style={{}}
                  disabled={loading}
                  dataTestId="sidebar-header-buttons-accounts-button"
                >
                  <Trans>Accounts</Trans>
                </Button>
              </>
            ) : (
              <Button
                variant="outlined"
                onClick={() => handleSidebarSection(SidebarKeys.siteList)}
                dataTestId="sidebar-header-buttons-back-to-site-list-button"
              >
                <Trans>Back to Site List</Trans>
              </Button>
            )}
          </>
        }
        truncateTitle
        dataTestId={`sidebar-header`}
      />

      {showSidebarData && sidebarData ? content.component : <LoadingData />}
    </div>
  );
};
